import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../api/client";
import "./LoanForm.css";
import LoanFormTab1 from "./LoanFormTab1";
import LoanFormTab2 from "./LoanFormTab2";
import LoanFormTab3 from "./LoanFormTab3";
import LoanFormTab4 from "./LoanFormTab4";
import { itemsToPayload } from "./BasketSecuritySection";

function addMonthsToDate(fromDate, months) {
  const d = new Date(fromDate || new Date());
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split("T")[0];
}

const CLASSIFICATION_OPTIONS = [
  { value: "BUSINESS_CREDIT", label: "אשראי עסקי" },
  { value: "INCOME_REAL_ESTATE", label: 'נדל"ן מניב' },
  { value: "CONSTRUCTION_FINANCE", label: "מימון בנייה" },
  { value: "LAND_LOAN", label: "הלוואת קרקע" },
  { value: "LOAN", label: "הלוואה" },
  { value: "CREDIT_FACILITY", label: "מסגרת אשראי" },
  { value: "OTHER", label: "אחר" },
];

// Maps each field name to its tab index (0-3)
const FIELD_TAB_MAP = {
  // Tab 0: Core
  classification: 0, classificationOtherReason: 0, lender: 0, borrowerCompany: 0,
  bankAccountNumber: 0, currency: 0, principal: 0, originationDate: 0, maturityDate: 0,
  rateType: 0, hasBaseRateCondition: 0, baseRateId: 0, baseRateConditionId1: 0,
  baseRateConditionId2: 0, margin: 0, hasVAT: 0, interestRate: 0, dayCountConvention: 0,
  // Tab 1: Fees & Terms
  originationFeePct: 1, unusedCommitmentFeePct: 1, prepaymentPenaltyPct: 1, managementFeePct: 1,
  drawnAmount: 1,
  loanType: 1, paymentPeriod: 1, hasEarlyRepaymentOption: 1, earlyRepaymentNoticeDays: 1,
  canExtend: 1, projectName: 1, remainingPrincipal: 1, hasRestrictedElement: 1,
  restrictedElementAmount: 1, restrictedElementReason: 1,
  // Tab 2: Security. The LTV/covenant fields below are NOT Loan schema paths — on
  // create they are posted flat and basketService.pickLtvConfig copies them onto the
  // new basket. cashDepositAmount is the one real Loan field here.
  canReleaseStockAtThreshold: 2, stockReleaseLTVMin: 2,
  stockReleaseLTVMax: 2,
  ltvPriceFormula: 2, ltvWarningThreshold: 2, ltvBreachThreshold: 2,
  cashDepositAmount: 2, covenantText: 2,
  // Tab 3: Notes
  disclosures: 3, notes: 3, generalNotes: 3,
};

// Helper functions for number formatting with commas
const formatNumberWithCommas = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  // Format with commas, allow decimals
  const parts = num.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
};

const parseNumberFromFormatted = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  // Remove commas and parse, allow decimals
  const cleaned = String(value).replace(/,/g, "").trim();
  if (cleaned === "" || cleaned === "-") return "";
  const num = Number(cleaned);
  return Number.isNaN(num) ? "" : num;
};

export default function LoanForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loan, setLoan] = useState({
    classification: "BUSINESS_CREDIT",
    classificationOtherReason: "",

    lender: "",
    borrowerCompany: "",
    bankAccountNumber: "",

    loanType: "",
    paymentPeriod: "",
    projectName: "",

    currency: "ILS",
    principal: 10000000,
    remainingPrincipal: "",

    originationDate: isEditMode ? "" : new Date().toISOString().split("T")[0],
    originationFeePct: 0,
    unusedCommitmentFeePct: 0,
    drawnAmount: 0,
    prepaymentPenaltyPct: 0,
    managementFeePct: 0,

    hasEarlyRepaymentOption: false,
    earlyRepaymentNoticeDays: 0,

    canExtend: false,

    baseRateId: "",
    baseRateName: "",

    hasBaseRateCondition: false,
    baseRateConditionId1: "",
    baseRateConditionId2: "",

    rateType: "FLOATING",
    margin: 0,
    interestRate: "",
    hasVAT: false,

    canReleaseStockAtThreshold: false,
    stockReleaseLTVMin: "",
    stockReleaseLTVMax: "",


    dayCountConvention: "365",

    maturityDate: "",

    disclosures: "",

    notes: "",
    generalNotes: "",

    hasRestrictedElement: false,
    restrictedElementAmount: 0,
    restrictedElementReason: "",

    ltvPriceFormula: "SPOT",
    ltvWarningThreshold: "",
    ltvBreachThreshold: "",
    cashDepositAmount: 0,
    covenantText: "",
    basket: null,
  });

  // Track formatted display values for number inputs (with commas)
  const [displayValues, setDisplayValues] = useState({
    principal: formatNumberWithCommas(10000000),
    remainingPrincipal: "",
    originationFeePct: "0",
    unusedCommitmentFeePct: "0",
    drawnAmount: "0",
    prepaymentPenaltyPct: "0",
    managementFeePct: "0",
    margin: "0",
    interestRate: "",
    earlyRepaymentNoticeDays: "0",
    stockReleaseLTVMin: "",
    stockReleaseLTVMax: "",
    restrictedElementAmount: "0",
    cashDepositAmount: "0",
    ltvWarningThreshold: "",
    ltvBreachThreshold: "",
  });

  const [security, setSecurity] = useState({
    basketMode: "STANDALONE",
    basketId: null,
    newBasketName: "",
    collateralItems: [],
  });

  const [activeTab, setActiveTab] = useState(0);
  const [baseRates, setBaseRates] = useState([]);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [missingFields, setMissingFields] = useState([]);
  useEffect(() => {
    apiClient.get("/baseRates")
      .then(res => setBaseRates(res.data))
      .catch(err => console.error(err));
  }, []);

  // Load existing loan for edit mode
  useEffect(() => {
    if (!isEditMode) return;

    apiClient
      .get(`/loans/${id}`)
      .then((res) => {
        const data = res.data;
        setLoan((prev) => ({
          ...prev,
          classification: data.classification || prev.classification,
          classificationOtherReason: data.classificationOtherReason || "",
          lender: data.lender || "",
          borrowerCompany: data.borrowerCompany || "",
          bankAccountNumber: data.bankAccountNumber || "",
          loanType: data.loanType || "",
          paymentPeriod: data.paymentPeriod || "",
          projectName: data.projectName || "",
          currency: data.currency || prev.currency,
          principal: data.principal ?? prev.principal,
          remainingPrincipal: data.remainingPrincipal ?? "",
          originationDate: data.originationDate
            ? data.originationDate.slice(0, 10)
            : "",
          originationFeePct: data.originationFeePct ?? 0,
          unusedCommitmentFeePct: data.unusedCommitmentFeePct ?? 0,
          drawnAmount: data.drawnAmount ?? 0,
          prepaymentPenaltyPct: data.prepaymentPenaltyPct ?? 0,
          managementFeePct: data.managementFeePct ?? 0,
          hasEarlyRepaymentOption: data.hasEarlyRepaymentOption ?? false,
          earlyRepaymentNoticeDays: data.earlyRepaymentNoticeDays ?? 0,
          canExtend: data.canExtend ?? false,
          baseRateId: data.baseRate ? String(data.baseRate) : "",
          baseRateName: data.baseRateName || "",
          hasBaseRateCondition: data.hasBaseRateCondition ?? false,
          baseRateConditionId1: data.baseRateConditionId1 ? String(data.baseRateConditionId1) : "",
          baseRateConditionId2: data.baseRateConditionId2 ? String(data.baseRateConditionId2) : "",
          rateType: data.rateType || prev.rateType,
          margin: data.margin ?? 0,
          interestRate: data.interestRate ?? "",
          hasVAT: data.hasVAT ?? false,
          // LTV/covenant config lives on the basket now (GET /loans/:id attaches it
          // as data.basket via enrichLoansWithBasketLTV), not flat on the loan.
          canReleaseStockAtThreshold: data.basket?.canReleaseStockAtThreshold ?? false,
          stockReleaseLTVMin: data.basket?.stockReleaseLTVMin ?? "",
          stockReleaseLTVMax: data.basket?.stockReleaseLTVMax ?? "",
          dayCountConvention: data.dayCountConvention === "360" ? "360" : "365",
          maturityDate: data.maturityDate ? data.maturityDate.slice(0, 10) : "",
          disclosures: data.disclosures || "",
          notes: data.notes || "",
          generalNotes: data.generalNotes || "",
          hasRestrictedElement: data.hasRestrictedElement ?? false,
          restrictedElementAmount: data.restrictedElementAmount ?? 0,
          restrictedElementReason: data.restrictedElementReason || "",
          ltvPriceFormula: data.basket?.ltvPriceFormula || "SPOT",
          ltvWarningThreshold: data.basket?.ltvWarningThreshold ?? "",
          ltvBreachThreshold: data.basket?.ltvBreachThreshold ?? "",
          cashDepositAmount: data.cashDepositAmount ?? 0,
          covenantText: data.basket?.covenantText || "",
          basket: data.basket || null,
        }));
        // Update display values with formatted numbers
        setDisplayValues({
          principal: formatNumberWithCommas(data.principal ?? 10000000),
          remainingPrincipal: formatNumberWithCommas(data.remainingPrincipal ?? ""),
          originationFeePct: formatNumberWithCommas(data.originationFeePct ?? 0),
          unusedCommitmentFeePct: formatNumberWithCommas(data.unusedCommitmentFeePct ?? 0),
          drawnAmount: formatNumberWithCommas(data.drawnAmount ?? 0),
          prepaymentPenaltyPct: formatNumberWithCommas(data.prepaymentPenaltyPct ?? 0),
          managementFeePct: formatNumberWithCommas(data.managementFeePct ?? 0),
          margin: formatNumberWithCommas(data.margin ?? 0),
          interestRate: formatNumberWithCommas(data.interestRate ?? ""),
          earlyRepaymentNoticeDays: formatNumberWithCommas(data.earlyRepaymentNoticeDays ?? 0),
          stockReleaseLTVMin: formatNumberWithCommas(data.basket?.stockReleaseLTVMin ?? ""),
          stockReleaseLTVMax: formatNumberWithCommas(data.basket?.stockReleaseLTVMax ?? ""),
          restrictedElementAmount: formatNumberWithCommas(data.restrictedElementAmount ?? 0),
          cashDepositAmount: formatNumberWithCommas(data.cashDepositAmount ?? 0),
          ltvWarningThreshold: formatNumberWithCommas(data.basket?.ltvWarningThreshold ?? ""),
          ltvBreachThreshold: formatNumberWithCommas(data.basket?.ltvBreachThreshold ?? ""),
        });
      })
      .catch((err) => console.error(err));
  }, [id, isEditMode]);
  useEffect(() => {
    // Fixed-rate loans: user enters interestRate directly, no auto-calculation
    if (loan.rateType === "FIXED") return;

    let baseRateValue = 0;

    if (loan.hasBaseRateCondition && loan.baseRateConditionId1 && loan.baseRateConditionId2) {
      // If there's a condition, use the minimum of the two rates
      const rate1 = baseRates.find(br => br._id === loan.baseRateConditionId1);
      const rate2 = baseRates.find(br => br._id === loan.baseRateConditionId2);
      const value1 = rate1 ? Number(rate1.value) : Infinity;
      const value2 = rate2 ? Number(rate2.value) : Infinity;
      baseRateValue = Math.min(value1, value2);
    } else {
      // Otherwise use the selected base rate
      const selectedBaseRate = baseRates.find(br => br._id === loan.baseRateId);
      baseRateValue = selectedBaseRate ? Number(selectedBaseRate.value) : 0;
    }
    
    const marginValue = Number(loan.margin || 0);
    const baseInterestRate = baseRateValue + marginValue;
    const VAT_RATE = 0.18; // 18% VAT - hardcoded as per requirements
    const calculated = loan.hasVAT 
      ? baseInterestRate * (1 + VAT_RATE)
      : baseInterestRate;

    setLoan(prev => ({
      ...prev,
      interestRate: calculated
    }));
  }, [loan.baseRateId, loan.hasBaseRateCondition, loan.baseRateConditionId1, loan.baseRateConditionId2, loan.margin, loan.hasVAT, loan.rateType, baseRates]);

  const tabHasErrors = (tabIndex) =>
    Object.entries(errors).some(([field, err]) => err && FIELD_TAB_MAP[field] === tabIndex);

  // Map field names to Hebrew labels for notifications
  const fieldLabels = {
    classification: "סיווג",
    lender: "שם מלווה",
    borrowerCompany: "שם חברה לווה",
    currency: "מטבע",
    principal: "קרן",
    baseRateId: "בסיס ריבית",
    rateType: "סוג ריבית",
    interestRate: "ריבית כוללת",
    classificationOtherReason: "סיבה לסיווג אחר",
    earlyRepaymentNoticeDays: "מספר ימי הודעה מראש לפירעון מוקדם",
    stockReleaseLTVMin: "LTV מינימלי לשחרור מניות",
    stockReleaseLTVMax: "LTV מקסימלי לשחרור מניות",
  };

  const validate = (fieldValues = loan, displayVals = displayValues) => {
    let temp = { ...errors };
    const missing = [];

    if ("principal" in fieldValues) {
      if (fieldValues.principal <= 0) {
        temp.principal = "הקרן צריכה להיות מעל 0";
        missing.push(fieldLabels.principal);
      } else {
        temp.principal = "";
      }
    }

    if ("margin" in fieldValues)
      temp.margin = "";

    if (fieldValues.rateType === "FIXED") {
      temp.baseRateId = "";
      temp.baseRateConditionId1 = "";
      temp.baseRateConditionId2 = "";
    } else if ("baseRateId" in fieldValues && fieldValues.rateType === "FLOATING") {
      if (!fieldValues.baseRateId) {
        temp.baseRateId = "יש לבחור בסיס ריבית";
        missing.push(fieldLabels.baseRateId);
      } else {
        temp.baseRateId = "";
      }
    }

    if ("currency" in fieldValues) {
      if (!fieldValues.currency) {
        temp.currency = "יש לבחור מטבע";
        missing.push(fieldLabels.currency);
      } else {
        temp.currency = "";
      }
    }

    if ("classification" in fieldValues) {
      if (!fieldValues.classification) {
        temp.classification = "יש לבחור סיווג";
        missing.push(fieldLabels.classification);
      } else {
        temp.classification = "";
      }
    }

    if ("classificationOtherReason" in fieldValues) {
      if (loan.classification === "OTHER" && !fieldValues.classificationOtherReason) {
        temp.classificationOtherReason = "יש לציין סיבה לסיווג אחר";
        missing.push(fieldLabels.classificationOtherReason);
      } else {
        temp.classificationOtherReason = "";
      }
    }

    if ("interestRate" in fieldValues) {
      temp.interestRate = "";
    }

    if ("lender" in fieldValues) {
      if (!fieldValues.lender || fieldValues.lender.trim() === "") {
        temp.lender = "יש להזין שם מלווה";
        missing.push(fieldLabels.lender);
      } else {
        temp.lender = "";
      }
    }

    if ("borrowerCompany" in fieldValues) {
      if (!fieldValues.borrowerCompany || fieldValues.borrowerCompany.trim() === "") {
        temp.borrowerCompany = "יש להזין שם חברה";
        missing.push(fieldLabels.borrowerCompany);
      } else {
        temp.borrowerCompany = "";
      }
    }

    if ("rateType" in fieldValues) {
      if (!fieldValues.rateType) {
        temp.rateType = "יש לבחור סוג ריבית";
        missing.push(fieldLabels.rateType);
      } else {
        temp.rateType = "";
      }
    }

    // Validate conditional fields
    if (fieldValues.hasEarlyRepaymentOption) {
      const noticeDays = parseNumberFromFormatted(displayVals.earlyRepaymentNoticeDays || "");
      if (noticeDays === "" || noticeDays === null || noticeDays === undefined || isNaN(noticeDays)) {
        temp.earlyRepaymentNoticeDays = "יש להזין מספר ימי הודעה מראש";
        missing.push(fieldLabels.earlyRepaymentNoticeDays);
      } else {
        temp.earlyRepaymentNoticeDays = "";
      }
    }

    if (fieldValues.canReleaseStockAtThreshold) {
      const minLTV = parseNumberFromFormatted(displayVals.stockReleaseLTVMin || "");
      const maxLTV = parseNumberFromFormatted(displayVals.stockReleaseLTVMax || "");
      if (minLTV === "" || minLTV === null || minLTV === undefined || isNaN(minLTV)) {
        temp.stockReleaseLTVMin = "יש להזין LTV מינימלי";
        missing.push(fieldLabels.stockReleaseLTVMin);
      } else {
        temp.stockReleaseLTVMin = "";
      }
      if (maxLTV === "" || maxLTV === null || maxLTV === undefined || isNaN(maxLTV)) {
        temp.stockReleaseLTVMax = "יש להזין LTV מקסימלי";
        missing.push(fieldLabels.stockReleaseLTVMax);
      } else {
        temp.stockReleaseLTVMax = "";
      }
    }

    // Validate base rate condition (only for floating rate loans)
    if (fieldValues.rateType === "FLOATING" && fieldValues.hasBaseRateCondition) {
      if (!fieldValues.baseRateConditionId1) {
        temp.baseRateConditionId1 = "יש לבחור ריבית בסיס ראשונה";
        missing.push("ריבית בסיס ראשונה");
      } else {
        temp.baseRateConditionId1 = "";
      }
      if (!fieldValues.baseRateConditionId2) {
        temp.baseRateConditionId2 = "יש לבחור ריבית בסיס שנייה";
        missing.push("ריבית בסיס שנייה");
      } else {
        temp.baseRateConditionId2 = "";
      }
    } else if (fieldValues.rateType === "FLOATING") {
      // If no condition but floating, baseRateId is required
      if ("baseRateId" in fieldValues && !fieldValues.baseRateId) {
        temp.baseRateId = "יש לבחור בסיס ריבית";
        missing.push(fieldLabels.baseRateId);
      }
    }

    // Validate restricted element
    if (fieldValues.hasRestrictedElement) {
      const restrictedAmount = parseNumberFromFormatted(displayVals.restrictedElementAmount || "");
      if (restrictedAmount === "" || restrictedAmount === null || restrictedAmount === undefined || isNaN(restrictedAmount) || restrictedAmount <= 0) {
        temp.restrictedElementAmount = "יש להזין סכום החלק המוגבל";
        missing.push("סכום החלק המוגבל");
      } else {
        temp.restrictedElementAmount = "";
      }
      if (!fieldValues.restrictedElementReason || fieldValues.restrictedElementReason.trim() === "") {
        temp.restrictedElementReason = "יש להזין סיבת ההגבלה";
        missing.push("סיבת ההגבלה");
      } else {
        temp.restrictedElementReason = "";
      }
    }

    setErrors({
      ...temp
    });

    // Remove duplicates from missing array
    const uniqueMissing = [...new Set(missing)];
    setMissingFields(uniqueMissing);

    return { isValid: Object.values(temp).every(x => x === ""), errors: temp };
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });
    validate({ [name]: loan[name] });
  };

  // Fields that should have comma formatting
  const numberFields = [
    "principal",
    "remainingPrincipal",
    "originationFeePct",
    "unusedCommitmentFeePct",
    "drawnAmount",
    "prepaymentPenaltyPct",
    "managementFeePct",
    "margin",
    "interestRate",
    "earlyRepaymentNoticeDays",
    "stockReleaseLTVMin",
    "stockReleaseLTVMax",
    "restrictedElementAmount",
    "cashDepositAmount",
    "ltvWarningThreshold",
    "ltvBreachThreshold",
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === "checkbox") {
      // Handle checkboxes
      setLoan({ ...loan, [name]: checked });
    } else if (numberFields.includes(name)) {
      // Allow user to type freely (numbers, commas, decimals)
      // Store the raw input temporarily for display
      setDisplayValues({ ...displayValues, [name]: value });
      
      // Parse to get numeric value (for state and validation)
      const numericValue = parseNumberFromFormatted(value);
      setLoan({ ...loan, [name]: numericValue });
    } else {
      // Non-number fields: update directly
      setLoan({ ...loan, [name]: value });
    }
  };

  const handleBlurNumber = (e) => {
    const { name } = e.target;
    if (numberFields.includes(name)) {
      // Format the display value on blur
      const formatted = formatNumberWithCommas(loan[name]);
      setDisplayValues({ ...displayValues, [name]: formatted });
    }
    handleBlur(e);
  };

  const handleMaturityShortcut = (months) => {
    const newDate = addMonthsToDate(loan.originationDate, months);
    setLoan(prev => ({ ...prev, maturityDate: newDate }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate all fields before submission
    const { isValid, errors: tempErrors } = validate(loan, displayValues);
    if (!isValid) {
      const firstErrorTab = [0, 1, 2, 3].find((t) =>
        Object.entries(tempErrors).some(([field, err]) => err && FIELD_TAB_MAP[field] === t)
      );
      if (firstErrorTab !== undefined) setActiveTab(firstErrorTab);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Clean payload: remove empty strings for optional fields, handle conditional fields
    const payload = { ...loan };

    // Convert empty strings to undefined/null for optional number fields
    if (payload.remainingPrincipal === "" || payload.remainingPrincipal === null || payload.remainingPrincipal === undefined) {
      delete payload.remainingPrincipal;
    }

    // Handle early repayment option: if false, don't send noticeDays
    if (!payload.hasEarlyRepaymentOption) {
      delete payload.earlyRepaymentNoticeDays;
    } else {
      // If true, ensure it's a valid number
      const noticeDays = parseNumberFromFormatted(displayValues.earlyRepaymentNoticeDays || "0");
      if (noticeDays === "" || noticeDays === null || noticeDays === undefined || isNaN(noticeDays)) {
        alert("יש להזין מספר ימי הודעה מראש לפירעון מוקדם");
        return;
      }
      payload.earlyRepaymentNoticeDays = noticeDays;
    }

    // Handle stock release threshold fields
    if (!payload.canReleaseStockAtThreshold) {
      delete payload.stockReleaseLTVMin;
      delete payload.stockReleaseLTVMax;
    } else {
      const minLTV = parseNumberFromFormatted(displayValues.stockReleaseLTVMin || "");
      const maxLTV = parseNumberFromFormatted(displayValues.stockReleaseLTVMax || "");
      const ltvErrors = {};
      if (minLTV === "" || minLTV === null || minLTV === undefined || isNaN(minLTV)) {
        ltvErrors.stockReleaseLTVMin = "יש להזין LTV מינימלי לשחרור מניות";
      }
      if (maxLTV === "" || maxLTV === null || maxLTV === undefined || isNaN(maxLTV)) {
        ltvErrors.stockReleaseLTVMax = "יש להזין LTV מקסימלי לשחרור מניות";
      }
      if (Object.keys(ltvErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...ltvErrors }));
        setTouched(prev => ({ ...prev, stockReleaseLTVMin: true, stockReleaseLTVMax: true }));
        setActiveTab(2);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      payload.stockReleaseLTVMin = minLTV;
      payload.stockReleaseLTVMax = maxLTV;
    }

    // Threshold fields ride along in the POST body so basketService.pickLtvConfig
    // can copy them onto the loan's new basket — they are not Loan schema paths.
    const warningThresh = parseNumberFromFormatted(displayValues.ltvWarningThreshold || "");
    payload.ltvWarningThreshold = (warningThresh !== "" && !isNaN(warningThresh)) ? warningThresh : null;

    const breachThresh = parseNumberFromFormatted(displayValues.ltvBreachThreshold || "");
    payload.ltvBreachThreshold = (breachThresh !== "" && !isNaN(breachThresh)) ? breachThresh : null;

    const cashDep = parseNumberFromFormatted(displayValues.cashDepositAmount || "");
    payload.cashDepositAmount = (cashDep !== "" && !isNaN(cashDep)) ? cashDep : 0;

    if (!payload.ltvPriceFormula) {
      payload.ltvPriceFormula = "SPOT";
    }

    if (!payload.covenantText || payload.covenantText.trim() === "") {
      delete payload.covenantText;
    }

    // Remove empty strings for optional text fields
    if (!payload.disclosures || payload.disclosures.trim() === "") {
      delete payload.disclosures;
    }
    if (!payload.notes || payload.notes.trim() === "") {
      delete payload.notes;
    }
    if (!payload.generalNotes || payload.generalNotes.trim() === "") {
      delete payload.generalNotes;
    }
    if (payload.classification !== "OTHER" || !payload.classificationOtherReason || payload.classificationOtherReason.trim() === "") {
      delete payload.classificationOtherReason;
    }

    // Handle base rate condition (only relevant for floating rates)
    if (payload.rateType === "FIXED") {
      delete payload.baseRateId;
      delete payload.baseRateName;
      delete payload.baseRateConditionId1;
      delete payload.baseRateConditionId2;
      delete payload.hasBaseRateCondition;
    } else if (payload.hasBaseRateCondition) {
      // If using condition, remove baseRateId and use condition rates
      delete payload.baseRateId;
      delete payload.baseRateName;
      if (!payload.baseRateConditionId1 || !payload.baseRateConditionId2) {
        alert("יש לבחור שתי ריביות בסיס כאשר יש תנאי ריבית");
        return;
      }
    } else {
      // If not using condition, remove condition fields and ensure baseRateId is set
      delete payload.baseRateConditionId1;
      delete payload.baseRateConditionId2;
      if (!payload.baseRateId) {
        alert("יש לבחור בסיס ריבית");
        return;
      }
    }

    // Handle restricted element
    if (payload.hasRestrictedElement) {
      const restrictedAmount = parseNumberFromFormatted(displayValues.restrictedElementAmount || "");
      if (restrictedAmount === "" || restrictedAmount === null || restrictedAmount === undefined || isNaN(restrictedAmount) || restrictedAmount <= 0) {
        alert("יש להזין סכום החלק המוגבל");
        return;
      }
      payload.restrictedElementAmount = restrictedAmount;
      if (!payload.restrictedElementReason || payload.restrictedElementReason.trim() === "") {
        alert("יש להזין סיבת ההגבלה");
        return;
      }
    } else {
      delete payload.restrictedElementAmount;
      delete payload.restrictedElementReason;
    }

    // Clean up any remaining empty string values for number fields
    Object.keys(payload).forEach(key => {
      if (numberFields.includes(key) && (payload[key] === "" || payload[key] === null)) {
        delete payload[key];
      }
    });

    try {
      if (isEditMode) {
        // EDIT: security is edited via the basket/pledge routes (LoanSecuritySection
        // on the loan detail page), never through the loan PUT. The LTV/covenant
        // fields below only mean anything on CREATE, where pickLtvConfig reads them.
        delete payload.basketId;
        delete payload.pledgeItems;
        delete payload.guaranteeItems;
        for (const f of [
          "ltvWarningThreshold", "ltvBreachThreshold", "ltvPriceFormula",
          "covenantText", "canReleaseStockAtThreshold",
          "stockReleaseLTVMin", "stockReleaseLTVMax",
        ]) delete payload[f];
        await apiClient.put(`/loans/${id}`, payload);
      } else {
        // CREATE: resolve basketId from security state
        let basketId = null;
        let createdBasketId = null;
        if (security.basketMode === "EXISTING") {
          if (!security.basketId) {
            setErrors(prev => ({ ...prev, basketId: "יש לבחור מסגרת" }));
            setActiveTab(2);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
          basketId = security.basketId;
        } else if (security.basketMode === "NEW") {
          if (!security.newBasketName.trim()) {
            setErrors(prev => ({ ...prev, newBasketName: "שם המסגרת חובה" }));
            setActiveTab(2);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
          const basketRes = await apiClient.post("/baskets", {
            name: security.newBasketName.trim(),
            lender: payload.lender,
            borrowerCompany: payload.borrowerCompany,
            ltvWarningThreshold: payload.ltvWarningThreshold,
            ltvBreachThreshold: payload.ltvBreachThreshold,
            ltvPriceFormula: payload.ltvPriceFormula,
            covenantText: payload.covenantText,
          });
          basketId = basketRes.data._id;
          createdBasketId = basketId;
        }
        const { pledgeItems, guaranteeItems } = itemsToPayload(security.collateralItems);
        try {
          await apiClient.post("/loans", { ...payload, basketId, pledgeItems, guaranteeItems });
        } catch (loanErr) {
          if (createdBasketId) {
            await apiClient.delete(`/baskets/${createdBasketId}`).catch(() => {});
          }
          throw loanErr;
        }
      }

      alert(isEditMode ? "ההלוואה עודכנה בהצלחה!" : "ההלוואה נוצרה בהצלחה!");
      navigate("/");
    } catch (err) {
      console.error(err);
      const errorCode = err.response?.data?.error;
      const errorMsg = errorCode === "OVER_PLEDGED"
        ? "לא ניתן לשעבד יותר מניות מסך האחזקה"
        : (errorCode || err.message || "שגיאה בשמירת ההלוואה");
      alert(errorMsg);
    }
  };

  const TAB_LABELS = ["פרטים עיקריים", "עמלות ותנאים", "בטחונות", "הערות"];

  // Compute maturity duration for hero
  const computeMaturityDisplay = () => {
    if (!loan.maturityDate) return null;
    const from = loan.originationDate ? new Date(loan.originationDate) : new Date();
    const to = new Date(loan.maturityDate);
    let years = to.getFullYear() - from.getFullYear();
    let months = to.getMonth() - from.getMonth();
    if (months < 0) { years--; months += 12; }
    if (years < 0) return null;
    if (years === 0 && months === 0) return "פחות מחודש";
    const parts = [];
    if (years > 0) parts.push(`${years}Y`);
    if (months > 0) parts.push(`${months}M`);
    return parts.join(" ");
  };
  const maturityDisplay = computeMaturityDisplay();

  // Compute base rate label for hero
  const heroBaseLabel = (() => {
    if (loan.hasBaseRateCondition && loan.baseRateConditionId1 && loan.baseRateConditionId2) {
      const r1 = baseRates.find(b => b._id === loan.baseRateConditionId1);
      const r2 = baseRates.find(b => b._id === loan.baseRateConditionId2);
      if (r1 && r2) return `min(${r1.name}, ${r2.name})`;
    }
    const br = baseRates.find(b => b._id === loan.baseRateId);
    return br ? br.name : null;
  })();

  const heroRateDisplay = (() => {
    if (!heroBaseLabel) return null;
    const margin = Number(loan.margin || 0);
    if (margin === 0) return heroBaseLabel;
    const sign = margin > 0 ? "+" : "−";
    return `${heroBaseLabel} ${sign} ${Math.abs(margin).toFixed(2)}%`;
  })();

  const currencySymbol = { ILS: "₪", USD: "$", EUR: "€", GBP: "£" }[loan.currency] || loan.currency;
  const negativeRateWarning = Number(loan.interestRate) < 0;

  return (
    <div className="loan-form-page">
      <div className="card">

        {/* ── Hero ─────────────────────────────────────────── */}
        <div className="lf-hero">

          {/* Identity bar */}
          <div className="lf-hero-identity">
            <div className="lf-hero-parties">
              <input
                className="lf-party-input"
                name="lender"
                value={loan.lender}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="שם מלווה"
                aria-label="שם מלווה"
              />
              <span className="lf-party-arrow">←</span>
              <input
                className="lf-party-input"
                name="borrowerCompany"
                value={loan.borrowerCompany}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="שם חברה לווה"
                aria-label="שם חברה לווה"
              />
              {loan.bankAccountNumber && (
                <span className="lf-bank-chip">⬡ {loan.bankAccountNumber}</span>
              )}
            </div>
            <div className="lf-hero-badges">
              <span className="lf-classification-badge">
                {CLASSIFICATION_OPTIONS.find(o => o.value === loan.classification)?.label || "—"}
              </span>
              <span className="lf-loan-status-dot" />
            </div>
          </div>

          {/* Metrics row */}
          <div className="lf-hero-metrics">
            <div className="lf-metric">
              <div className="lf-metric-value">
                {displayValues.principal
                  ? `${currencySymbol}${displayValues.principal}`
                  : <span className="lf-metric-empty">—</span>}
              </div>
              <div className="lf-metric-label">קרן</div>
            </div>

            <div className="lf-metric-sep" />

            <div className="lf-metric">
              <div className="lf-metric-value">
                {loan.interestRate !== "" && loan.interestRate !== null && loan.interestRate !== undefined
                  ? <span className={Number(loan.interestRate) < 0 ? "lf-metric-negative" : ""}>{Number(loan.interestRate).toFixed(2)}%</span>
                  : <span className="lf-metric-empty">—</span>}
              </div>
              <div className="lf-metric-label">ריבית כוללת</div>
            </div>

            <div className="lf-metric-sep" />

            <div className="lf-metric">
              <div className="lf-metric-value lf-metric-secondary">
                {heroRateDisplay || <span className="lf-metric-empty">—</span>}
              </div>
              <div className="lf-metric-label">בסיס + מרווח</div>
            </div>

            <div className="lf-metric-sep" />

            <div className="lf-metric">
              <div className="lf-metric-value">
                {maturityDisplay
                  ? maturityDisplay
                  : loan.maturityDate
                    ? <span className="lf-metric-secondary">{new Date(loan.maturityDate).toLocaleDateString("he-IL")}</span>
                    : <span className="lf-metric-empty">—</span>}
              </div>
              <div className="lf-metric-label">משך</div>
            </div>
          </div>

        </div>

        {/* Tab bar */}
        <div className="tabs loan-form-tabs">
          {TAB_LABELS.map((label, i) => (
            <button
              key={i}
              type="button"
              className={activeTab === i ? "active" : ""}
              onClick={() => setActiveTab(i)}
            >
              {tabHasErrors(i) && <span className="tab-error-badge" />}
              {label}
            </button>
          ))}
        </div>

        {/* Missing fields alert */}
        {missingFields.length > 0 && (
          <div className="alert-error">
            <strong>שדות חובה חסרים:</strong>
            <ul>
              {missingFields.map((field, idx) => (
                <li key={idx}>{field}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Tab content + submit */}
        <form onSubmit={handleSubmit} className="loan-form-tab-panel">
          <div style={{ display: activeTab === 0 ? "block" : "none" }}>
            <LoanFormTab1
              loan={loan}
              displayValues={displayValues}
              errors={errors}
              touched={touched}
              baseRates={baseRates}
              onChange={handleChange}
              onBlur={handleBlur}
              onBlurNumber={handleBlurNumber}
              negativeRateWarning={negativeRateWarning}
              onMaturityShortcut={handleMaturityShortcut}
            />
          </div>
          <div style={{ display: activeTab === 1 ? "block" : "none" }}>
            <LoanFormTab2
              loan={loan}
              displayValues={displayValues}
              errors={errors}
              touched={touched}
              onChange={handleChange}
              onBlur={handleBlur}
              onBlurNumber={handleBlurNumber}
            />
          </div>
          <div style={{ display: activeTab === 2 ? "block" : "none" }}>
            <LoanFormTab3
              loan={loan}
              displayValues={displayValues}
              errors={errors}
              touched={touched}
              onChange={handleChange}
              onBlur={handleBlur}
              onBlurNumber={handleBlurNumber}
              security={security}
              onChangeSecurity={setSecurity}
              isEditMode={isEditMode}
              existingBasket={loan.basket || null}
              loanId={id}
            />
          </div>
          <div style={{ display: activeTab === 3 ? "block" : "none" }}>
            <LoanFormTab4
              loan={loan}
              onChange={handleChange}
            />
          </div>

          <div className="loan-form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => navigate("/")}>
              ביטול
            </button>
            <button type="submit" className="btn btn-primary">
              {isEditMode ? "עדכון הלוואה" : "יצירת הלוואה"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
